import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {MatMenuTrigger} from '@angular/material/menu';
import {CodaltComponent} from '../codalt/codalt.component';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {fromEvent} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {DrawingService} from '../../services/drawing.service';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {DrawingsDataSource} from './drawing-data-source.service';
import {Drawing} from '../../classes/drawing';
import {Features} from '../../services/auth/features';
import {AuthorisationService} from '../../services/auth/authorisation.service';
import {Roles} from '../../services/auth/roles';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ImportDrawingDialogComponent} from './import-drawing-dialog/import-drawing-dialog.component';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {FormControl} from '@angular/forms';
import {Project} from '../../classes/project';
import {User} from '../../classes/user';
import {ConfirmDialogComponent} from '../../services/confirm-dialog-service/confirm-dialog/confirm-dialog.component';
import {LocalStorage} from '../../utils/storage';

@Component({
    selector: 'app-drawings',
    templateUrl: './drawings.component.html',
    styleUrls: ['./drawings.component.scss']
})
export class DrawingsComponent extends CodaltComponent implements OnInit, AfterViewInit {
    private readonly sessionKey = 'drawingsFormData';

    displayedColumns: string[] = ['updated_at', 'date', 'filename', 'drawer', 'project_id'];
    dataSource: DrawingsDataSource;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
    @ViewChild('svgMenuTrigger') svgMenuTrigger: MatMenuTrigger;
    @ViewChild('convertMenuTrigger') convertMenuTrigger: MatMenuTrigger;
    @ViewChild('dwgToJsonMenuTrigger') dwgToJsonMenuTrigger: MatMenuTrigger;
    contextMenuPosition = {x: 0, y: 0};
    contextMenuDrawing: Drawing | null = null;
    contextMenuConvertDrawing: Drawing | null = null;
    contextMenuDwgDrawing: Drawing | null = null;
    searchString: string;
    fcErrors = new FormControl(false);
    projectIdControl = new FormControl<number | null>(null);
    drawerInitialsControl = new FormControl<string | null>(null);
    projects: Project[] = [];
    drawers: User[] = [];
    drawerSearchFn = (term: string, item: User) => {
        const lower = term.toLowerCase();
        return item.initials?.toLowerCase().includes(lower) || item.name?.toLowerCase().includes(lower);
    };

    constructor(private drawingService: DrawingService,
                private confirmDialogService: ConfirmDialogService,
                private router: Router,
                private dialog: MatDialog,
                private route: ActivatedRoute,
                private title: Title) {
        super();
    }

    ngOnInit() {
        this.title.setTitle('Tekeningen - TSNed App');
        this.dataSource = new DrawingsDataSource(this.drawingService);

        const savedFormData = this.getFormDataFromSession();
        const savedFilters = savedFormData?.formData ?? savedFormData;
        if (savedFilters) {
            this.searchString = savedFilters.search ?? this.searchString;
            this.fcErrors.setValue(savedFilters.errors ?? false, {emitEvent: false});
            this.projectIdControl.setValue(savedFilters.projectId ?? null, {emitEvent: false});
            this.drawerInitialsControl.setValue(savedFilters.drawerInitials ?? null, {emitEvent: false});
        }

        if (AuthorisationService.hasRole(Roles.Drawer)) {
            const hasDrawerInitialsKey = savedFilters && Object.prototype.hasOwnProperty.call(savedFilters, 'drawerInitials');

            if (!hasDrawerInitialsKey && LocalStorage.user?.initials) {
                this.drawerInitialsControl.setValue(LocalStorage.user.initials, {emitEvent: false});
            }
        }

        this.subscriptions.add(this.route.queryParams.subscribe((queryParams: { search: string }) => {
            if (queryParams.search?.length > 0) {
                const searchValue = decodeURI(queryParams.search);
                const projectMatch = searchValue.match(/^P:(\d+)$/);
                if (projectMatch) {
                    this.projectIdControl.setValue(+projectMatch[1]);
                    this.searchString = '';
                    this.paginator?.firstPage();
                    this.dataSource.searchString = this.searchString;
                    this.router.navigate([], {queryParams: {search: null}, queryParamsHandling: 'merge'});
                } else {
                    this.searchString = searchValue;
                    this.paginator?.firstPage();
                    this.dataSource.searchString = this.searchString;
                }
                this.saveFormDataToSession();
            }
        }));
        this.subscriptions.add(this.drawingService.getFilters().subscribe(response => {
            this.projects = response.data.projects;
            this.drawers = response.data.drawers;
        }));
        this.subscriptions.add(this.fcErrors.valueChanges.subscribe(() => {
            this.dataSource.errors = this.fcErrors.value;
            this.saveFormDataToSession();
        }));
        this.subscriptions.add(this.projectIdControl.valueChanges.subscribe(projectId => {
            this.paginator?.firstPage();
            this.dataSource.projectId = projectId;
            this.saveFormDataToSession();
        }));
        this.subscriptions.add(this.drawerInitialsControl.valueChanges.subscribe(drawerInitials => {
            this.paginator?.firstPage();
            this.dataSource.drawerInitials = drawerInitials;
            this.saveFormDataToSession();
        }));

        if (AuthorisationService.hasFeature(Features.DrawingsConvertDetailSave)) {
            this.displayedColumns.push('dwgToJson');
        }
        if (AuthorisationService.hasFeature(Features.DrawingsEntitiesSave)) {
            this.displayedColumns.push('convert');
        }
        if (AuthorisationService.hasFeature(Features.DrawingsGenerateSvgsDetailSave)) {
            this.displayedColumns.push('dxfToSvg');
        }
    }

    dwgToJson(drawing: Drawing, onQueue = true, runSubsequent = false, event?: Event) {
        event?.preventDefault();
        const syncDialogRef = !onQueue ? this.openDwgToJsonDialog(drawing) : null;
        drawing['extracting'] = true;
        drawing.json_data_length = 0;
        this.subscriptions.add(this.drawingService.extract(drawing.id, onQueue, runSubsequent).subscribe(result => {
            Object.assign(drawing, result.data);
            drawing['extracting'] = false;
            this.finishDwgToJsonDialog(syncDialogRef);
        }, () => {
            drawing['extracting'] = false;
            this.failDwgToJsonDialog(syncDialogRef);
        }));
    }

    private openDwgToJsonDialog(drawing: Drawing): MatDialogRef<ConfirmDialogComponent> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            panelClass: 'confirm-dialog',
            disableClose: true,
            width: '500px',
            maxWidth: '95vw',
            data: {
                title: 'Conversie wordt uitgevoerd',
                text: `<i class="fa fa-spinner fa-spin me-2"></i>Tekening ${drawing.filename} wordt nu direct geconverteerd. Dit kan even duren.`,
                confirm: null,
                cancel: null
            }
        });

        const subscription = dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.openDrawing(drawing);
            }
            subscription.unsubscribe();
        });

        return dialogRef;
    }

    private finishDwgToJsonDialog(dialogRef?: MatDialogRef<ConfirmDialogComponent>) {
        if (!dialogRef) {
            return;
        }
        dialogRef.disableClose = false;
        dialogRef.componentInstance.data.title = 'Conversie afgerond';
        dialogRef.componentInstance.data.text = 'De tekening is geconverteerd.';
        dialogRef.componentInstance.data.confirm = 'Open tekening';
        dialogRef.componentInstance.data.cancel = 'Sluiten';
    }

    private failDwgToJsonDialog(dialogRef?: MatDialogRef<ConfirmDialogComponent>) {
        if (!dialogRef) {
            return;
        }
        dialogRef.disableClose = false;
        dialogRef.componentInstance.data.title = 'Conversie mislukt';
        dialogRef.componentInstance.data.text = 'De tekening kon niet worden geconverteerd.';
        dialogRef.componentInstance.data.confirm = null;
        dialogRef.componentInstance.data.cancel = 'Sluiten';
    }

    private openDrawing(drawing: Drawing) {
        this.router.navigate(['/', this.Routenames.Projects, drawing.project_id], {queryParams: {drawing: drawing.id}});
    }

    convert(drawing: Drawing, onQueue = true) {
        drawing.entities_count = 0;
        drawing['converting'] = drawing.id;
        this.subscriptions.add(this.drawingService.convert(drawing.id, onQueue).subscribe(result => {
            Object.assign(drawing, result.data);
            drawing['converting'] = null;
        }, () => {
            drawing['converting'] = null;
        }));
    }

    importDrawing() {
        this.dialog.open(ImportDrawingDialogComponent, {
            width: '700px',
            maxWidth: '98vw'
        }).afterClosed().subscribe(() => {
            this.paginator.firstPage();
            this.dataSource.loadDrawings();
        });
    }

    generateSvgs(drawing: Drawing, onQueue = true) {
        drawing['generatingSvgs'] = drawing.id;
        drawing.entity_images_count = 0;
        this.subscriptions.add(this.drawingService.generateSvgs(drawing.id, onQueue).subscribe(result => {
            Object.assign(drawing, result.data);
            drawing['generatingSvgs'] = null;
        }, () => {
            drawing['generatingSvgs'] = null;
        }));
    }

    openSvgContextMenu(event: MouseEvent, drawing: Drawing) {
        event.preventDefault();
        this.contextMenuPosition = {x: event.clientX, y: event.clientY};
        this.contextMenuDrawing = drawing;
        this.svgMenuTrigger.openMenu();
    }

    openConvertContextMenu(event: MouseEvent, drawing: Drawing) {
        event.preventDefault();
        this.contextMenuPosition = {x: event.clientX, y: event.clientY};
        this.contextMenuConvertDrawing = drawing;
        this.convertMenuTrigger.openMenu();
    }

    openDwgToJsonContextMenu(event: MouseEvent, drawing: Drawing) {
        event.preventDefault();
        if (!AuthorisationService.hasRole(Roles.Codalt)) {
            return;
        }
        this.contextMenuPosition = {x: event.clientX, y: event.clientY};
        this.contextMenuDwgDrawing = drawing;
        this.dwgToJsonMenuTrigger.openMenu();
    }

    ngAfterViewInit() {
        this.dataSource.pageSize = 100;
        this.dataSource.sort = this.sort;
        this.dataSource.errors = this.fcErrors.value;
        this.dataSource.projectId = this.projectIdControl.value;
        this.dataSource.drawerInitials = this.drawerInitialsControl.value;
        this.dataSource.searchString = this.searchString;
        this.dataSource.paginator = this.paginator;
        fromEvent<KeyboardEvent>(this.searchInput.nativeElement, 'keyup')
            .pipe(debounceTime(300)).subscribe(() => {
            this.paginator.firstPage();
            this.dataSource.searchString = this.searchString;
            this.saveFormDataToSession();
            this.router.navigate([], {queryParams: {search: this.searchString ? encodeURI(this.searchString) : null}, queryParamsHandling: 'merge'});
        });
    }

    override ngOnDestroy() {
        this.saveFormDataToSession();
        super.ngOnDestroy();
    }

    private getFormDataFromSession(): any | null {
        const raw = sessionStorage.getItem(this.sessionKey);
        if (raw === null) {
            return null;
        }
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private saveFormDataToSession(): void {
        sessionStorage.setItem(this.sessionKey, JSON.stringify({
            formData: {
                search: this.searchString,
                errors: this.fcErrors.value,
                projectId: this.projectIdControl.value,
                drawerInitials: this.drawerInitialsControl.value,
            },
        }));
    }
}
